import { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { ThemeProvider } from "@/components/ThemeProvider";
import { AuthProvider } from "@/hooks/useAuth";
import { JacKeyNudgeProvider } from "@/contexts/JacKeyNudgeContext";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { ScrollToTop } from "@/components/ScrollToTop";
import { GoogleAnalytics } from "@/components/GoogleAnalytics";
import { ActivityTracker } from "@/components/ActivityTracker";
import { toast } from "sonner";
import Index from "./pages/Index";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import CreateAnnouncement from "./pages/CreateAnnouncement";
import MobileAnnouncementCreator from "./pages/MobileAnnouncementCreator";
import EditAnnouncement from "./pages/EditAnnouncement";
import AnnouncementView from "./pages/AnnouncementView";
import Settings from "./pages/Settings";
import About from "./pages/About";
import FAQ from "./pages/FAQ";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import GiftStore from "./pages/GiftStore";
import PartnerRedirect from "./pages/PartnerRedirect";
import AdminWaitlist from "./pages/AdminWaitlist";
import AdminFounders from "./pages/AdminFounders";
import AdminSEO from "./pages/AdminSEO";
import Treslog from "./pages/Treslog";
import TreslogPublicView from "./pages/TreslogPublicView";
import AddCredits from "./pages/AddCredits";
import PrivateAnnouncementView from "./pages/PrivateAnnouncementView";
import PaymentSuccess from "./pages/PaymentSuccess";
import GiftSuggestions from "./pages/GiftSuggestions";
import AdminAffiliateAnalytics from "./pages/AdminAffiliateAnalytics";
import ReferAFriend from "./pages/ReferAFriend";
import AdminReferrals from "./pages/AdminReferrals";
import FoundersWall from "./pages/FoundersWall";
import NotFound from "./pages/NotFound";
import AnnouncementTokenView from "./pages/AnnouncementTokenView";
import Blog from "./pages/Blog";
import FounderSuggestions from "./pages/FounderSuggestions";
import TryCreate from "./pages/TryCreate";
import InviteAccess from "./pages/InviteAccess";
import AdminInfluencerDashboard from "./pages/AdminInfluencerDashboard";
import AdminInfluencerManager from "./pages/AdminInfluencerManager";

import AdminHub from "./pages/AdminHub";
import AdminManagement from "./pages/AdminManagement";
import AdminAllMembers from "./pages/AdminAllMembers";
import AdminAffirmations from "./pages/AdminAffirmations";
import PublicTreeView from "./pages/PublicTreeView";
import AffirmationImageGenerator from "./pages/AffirmationImageGenerator";
import AffirmationPublicView from "./pages/AffirmationPublicView";
import SavedMessages from "./pages/SavedMessages";
import AdminQA from "./pages/AdminQA";

// SEO landing pages
import HeartfeltMessageToMom from "./pages/seo/HeartfeltMessageToMom";
import HeartfeltMessageToDad from "./pages/seo/HeartfeltMessageToDad";
import MessageToSomeoneYouLove from "./pages/seo/MessageToSomeoneYouLove";
import ApologyMessageExamples from "./pages/seo/ApologyMessageExamples";
import ThankYouMessageForParents from "./pages/seo/ThankYouMessageForParents";
import WhatToSayBeforeSomeoneDies from "./pages/seo/WhatToSayBeforeSomeoneDies";
import LivingTributeIdeas from "./pages/seo/LivingTributeIdeas";
import DigitalLegacyMessage from "./pages/seo/DigitalLegacyMessage";
import WhatIsALivingAnnouncement from "./pages/seo/WhatIsALivingAnnouncement";
import WhySayItNowExists from "./pages/seo/WhySayItNowExists";
import DigitalGratitude from "./pages/seo/DigitalGratitude";
import AffirmationsAndEmotionalWellness from "./pages/seo/AffirmationsAndEmotionalWellness";
import LivingAnnouncementsIndex from "./pages/seo/LivingAnnouncementsIndex";
import LivingAnnouncementCategoryRoute from "./pages/seo/LivingAnnouncementCategoryRoute";

// Free tool pages
import HeartfeltMessageGenerator from "./pages/tools/HeartfeltMessageGenerator";
import ApologyMessageGenerator from "./pages/tools/ApologyMessageGenerator";
import ThankYouMessageGenerator from "./pages/tools/ThankYouMessageGenerator";

// Occasion landing pages
import BirthdayForMom from "./pages/occasions/BirthdayForMom";
import BirthdayForDad from "./pages/occasions/BirthdayForDad";
import RetirementTribute from "./pages/occasions/RetirementTribute";
import AnniversaryAppreciation from "./pages/occasions/AnniversaryAppreciation";
import LetterToAgingParent from "./pages/occasions/LetterToAgingParent";


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 30_000,
    },
  },
});

// Main App Component
const App = () => {
  // Global unhandled rejection handler — prevents query/fetch failures from crashing the app
  useEffect(() => {
    const handler = (event: PromiseRejectionEvent) => {
      console.error("[App] Unhandled promise rejection:", event.reason);
      // Do NOT force logout or redirect — just show an error toast
      toast.error("Something went wrong. Please try again.");
      event.preventDefault();
    };
    window.addEventListener("unhandledrejection", handler);
    return () => window.removeEventListener("unhandledrejection", handler);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
        <LanguageProvider>
          <AuthProvider>
            <JacKeyNudgeProvider>
              <TooltipProvider>
                <Toaster />
                <Sonner />
            <BrowserRouter>
              <ScrollToTop />
              <GoogleAnalytics />
              <ActivityTracker />
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/about" element={<About />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/gift-store" element={<GiftStore />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/try" element={<TryCreate />} />

                {/* SEO landing pages */}
                <Route path="/heartfelt-message-to-mom" element={<HeartfeltMessageToMom />} />
                <Route path="/heartfelt-message-to-dad" element={<HeartfeltMessageToDad />} />
                <Route path="/message-to-someone-you-love" element={<MessageToSomeoneYouLove />} />
                <Route path="/apology-message-examples" element={<ApologyMessageExamples />} />
                <Route path="/thank-you-message-for-parents" element={<ThankYouMessageForParents />} />
                <Route path="/what-to-say-before-someone-dies" element={<WhatToSayBeforeSomeoneDies />} />
                <Route path="/living-tribute-ideas" element={<LivingTributeIdeas />} />
                <Route path="/digital-legacy-message" element={<DigitalLegacyMessage />} />

                {/* Editorial authority pages */}
                <Route path="/what-is-a-living-announcement" element={<WhatIsALivingAnnouncement />} />
                <Route path="/why-say-it-now-exists" element={<WhySayItNowExists />} />
                <Route path="/digital-gratitude" element={<DigitalGratitude />} />
                <Route path="/affirmations-and-emotional-wellness" element={<AffirmationsAndEmotionalWellness />} />

                {/* Living Announcements public gallery */}
                <Route path="/living-announcements" element={<LivingAnnouncementsIndex />} />
                <Route path="/living-announcements/:slug" element={<LivingAnnouncementCategoryRoute />} />



                {/* Occasion landing pages */}
                <Route path="/birthday-for-mom" element={<BirthdayForMom />} />
                <Route path="/birthday-for-dad" element={<BirthdayForDad />} />
                <Route path="/retirement-tribute" element={<RetirementTribute />} />
                <Route path="/anniversary-appreciation" element={<AnniversaryAppreciation />} />
                <Route path="/letter-to-aging-parent" element={<LetterToAgingParent />} />

                {/* Free tool pages */}
                <Route path="/tools/heartfelt-message-generator" element={<HeartfeltMessageGenerator />} />
                <Route path="/tools/apology-message-generator" element={<ApologyMessageGenerator />} />
                <Route path="/tools/thank-you-message-generator" element={<ThankYouMessageGenerator />} />

                <Route
                  path="/admin/waitlist"
                  element={
                    <ProtectedRoute>
                      <AdminWaitlist />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/founders"
                  element={
                    <ProtectedRoute>
                      <AdminFounders />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/seo"
                  element={
                    <ProtectedRoute>
                      <AdminSEO />
                    </ProtectedRoute>
                  }
                />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/create"
                  element={
                    <ProtectedRoute>
                      <CreateAnnouncement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/create-quick"
              element={
                <ProtectedRoute>
                  <MobileAnnouncementCreator />
                </ProtectedRoute>
              }
                />
                <Route
                  path="/edit/:id"
                  element={
                    <ProtectedRoute>
                      <EditAnnouncement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <Settings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/treslog"
                  element={
                    <ProtectedRoute>
                      <Treslog />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/add-credits"
                  element={
                    <ProtectedRoute>
                      <AddCredits />
                    </ProtectedRoute>
                  }
                />
                <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
                <Route path="/a/private/:token" element={<PrivateAnnouncementView />} />
                <Route path="/a/token/:token" element={<AnnouncementTokenView />} />
                <Route path="/a/:slug" element={<AnnouncementView />} />
                <Route path="/treslog/:id" element={<TreslogPublicView />} />
                <Route path="/go/:slug" element={<PartnerRedirect />} />
                <Route
                  path="/announcement/:id/gift-suggestions"
                  element={
                    <ProtectedRoute>
                      <GiftSuggestions />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/affiliate-analytics"
                  element={
                    <ProtectedRoute>
                      <AdminAffiliateAnalytics />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/referrals"
                  element={
                    <ProtectedRoute>
                      <AdminReferrals />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/refer"
                  element={
                    <ProtectedRoute>
                      <ReferAFriend />
                    </ProtectedRoute>
                  }
                />
                <Route path="/founders" element={<FoundersWall />} />
                <Route
                  path="/founder-suggestions"
                  element={
                    <ProtectedRoute>
                      <FounderSuggestions />
                    </ProtectedRoute>
                  }
                />
                <Route path="/tree/:userId" element={<PublicTreeView />} />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <AdminHub />
                    </ProtectedRoute>
                  }
                />
                <Route path="/invite/:token" element={<InviteAccess />} />
                <Route
                  path="/admin/influencers"
                  element={
                    <ProtectedRoute>
                      <AdminInfluencerDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/influencer-manager"
                  element={
                    <ProtectedRoute>
                      <AdminInfluencerManager />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/manage"
                  element={
                    <ProtectedRoute>
                      <AdminManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/members"
                  element={
                    <ProtectedRoute>
                      <AdminAllMembers />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/affirmations"
                  element={
                    <ProtectedRoute>
                      <AdminAffirmations />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/qa"
                  element={
                    <ProtectedRoute>
                      <AdminQA />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/affirmation-images"
                  element={
                    <ProtectedRoute>
                      <AffirmationImageGenerator />
                    </ProtectedRoute>
                  }
                />
                <Route path="/affirmation/:id" element={<AffirmationPublicView />} />
                <Route
                  path="/saved"
                  element={
                    <ProtectedRoute>
                      <SavedMessages />
                    </ProtectedRoute>
                  }
                />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </BrowserRouter>
            </TooltipProvider>
            </JacKeyNudgeProvider>
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
